import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type SlotType = 'category' | 'product';
export type PricingRule = 'highest' | 'average' | 'fixed' | 'rodizio';

export interface ComboSlot {
  id: string;
  combo_id: string;
  slot_type: SlotType;
  category_id: string | null;
  product_id: string | null;
  min_quantity: number;
  max_quantity: number;
  pricing_rule: PricingRule;
  fixed_price: number;
  production_location_id: string | null;
  label: string | null;
  is_required: boolean;
  display_order: number;
  created_at: string;
  // Joined data
  category?: { id: string; name: string } | null;
  product?: { id: string; name: string; price: number } | null;
  production_location?: { id: string; name: string } | null;
}

export interface ComboSlotFormData {
  slot_type: SlotType;
  category_id?: string | null;
  product_id?: string | null;
  min_quantity: number;
  max_quantity: number;
  pricing_rule: PricingRule;
  fixed_price?: number;
  production_location_id?: string | null;
  label?: string | null;
  is_required: boolean;
  display_order?: number;
}

export function useComboSlots(comboId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: slots = [], isLoading, error } = useQuery({
    queryKey: ['combo-slots', comboId],
    queryFn: async () => {
      if (!comboId) return [];

      const { data, error } = await supabase
        .from('combo_slots')
        .select(`
          *,
          category:categories(id, name),
          product:products(id, name, price),
          production_location:production_locations(id, name)
        `)
        .eq('combo_id', comboId)
        .order('display_order');

      if (error) throw error;
      return data as ComboSlot[];
    },
    enabled: !!comboId,
  });

  const createSlot = useMutation({
    mutationFn: async (formData: ComboSlotFormData) => {
      if (!comboId) throw new Error('Combo ID é obrigatório');

      // Get max display_order
      const maxOrder = slots.reduce((max, s) => Math.max(max, s.display_order), -1);

      const { data, error } = await supabase
        .from('combo_slots')
        .insert({
          combo_id: comboId,
          slot_type: formData.slot_type,
          category_id: formData.slot_type === 'category' ? formData.category_id : null,
          product_id: formData.slot_type === 'product' ? formData.product_id : null,
          min_quantity: formData.min_quantity,
          max_quantity: formData.max_quantity,
          pricing_rule: formData.pricing_rule,
          fixed_price: formData.fixed_price || 0,
          production_location_id: formData.production_location_id || null,
          label: formData.label || null,
          is_required: formData.is_required,
          display_order: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combo-slots', comboId] });
      toast.success('Slot adicionado com sucesso');
    },
    onError: (error) => {
      console.error('Erro ao criar slot:', error);
      toast.error('Erro ao adicionar slot');
    },
  });

  const updateSlot = useMutation({
    mutationFn: async ({ slotId, formData }: { slotId: string; formData: Partial<ComboSlotFormData> }) => {
      const updateData: Record<string, unknown> = { ...formData };
      
      // Handle slot_type changes
      if (formData.slot_type === 'category') {
        updateData.product_id = null;
      } else if (formData.slot_type === 'product') {
        updateData.category_id = null;
      }

      const { data, error } = await supabase
        .from('combo_slots')
        .update(updateData)
        .eq('id', slotId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combo-slots', comboId] });
      toast.success('Slot atualizado com sucesso');
    },
    onError: (error) => {
      console.error('Erro ao atualizar slot:', error);
      toast.error('Erro ao atualizar slot');
    },
  });

  const deleteSlot = useMutation({
    mutationFn: async (slotId: string) => {
      const { error } = await supabase
        .from('combo_slots')
        .delete()
        .eq('id', slotId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combo-slots', comboId] });
      toast.success('Slot removido com sucesso');
    },
    onError: (error) => {
      console.error('Erro ao remover slot:', error);
      toast.error('Erro ao remover slot');
    },
  });

  const reorderSlots = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('combo_slots')
          .update({ display_order: index })
          .eq('id', id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['combo-slots', comboId] });
    },
    onError: (error) => {
      console.error('Erro ao reordenar slots:', error);
      toast.error('Erro ao reordenar slots');
    },
  });

  return {
    slots,
    isLoading,
    error,
    createSlot,
    updateSlot,
    deleteSlot,
    reorderSlots,
  };
}
